import { type FC, useLayoutEffect, useState } from "react"

import { type CommunityAccess, type CommunityChannelProductAccess } from "@forento/core/models/community"

import Button, { SubmitButton } from "~/components/Button"
import InputField from "~/components/InputField"
import { DefaultModal, ModalButtons, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useAlert } from "~/contexts/AlertContext"
import trpc, { query } from "~/utilities/trpc"

import ChannelAccessInputs, {
	type MembershipAccess,
	type ProductAccess,
	useChannelProducts,
} from "./ChannelAccessInputs"

type Props = {
	isOpen: boolean
	channel: {
		id: number | null
		name: string
		defaultAccess: CommunityAccess | null
		membershipAccess: { membershipId: number; access: CommunityAccess }[]
		productAccess: CommunityChannelProductAccess[]
	}
	onClose(): void
	onSubmitted(): Promise<unknown>
}
const EditModal: FC<Props> = ({ isOpen, channel, onClose, onSubmitted }) => {
	const alert = useAlert()

	const [name, setName] = useState("")
	const [defaultAccess, setDefaultAccess] = useState<CommunityAccess | null>("post")
	const [membershipAccess, setMembershipAccess] = useState<MembershipAccess[]>([])
	const [productAccess, setProductAccess] = useState<ProductAccess[]>([])
	const [isSubmitting, setSubmitting] = useState(false)

	const memberships = query.membership.list.useQuery()
	const products = useChannelProducts()

	useLayoutEffect(() => {
		setName(channel.name)
		setDefaultAccess(channel.defaultAccess)
		setMembershipAccess(channel.membershipAccess)
		setProductAccess(channel.productAccess)
	}, [channel])

	async function handleSubmit() {
		setSubmitting(true)
		try {
			await trpc.community.updateChannel.mutate({
				id: channel.id,
				data: { name, defaultAccess, membershipAccess, productAccess },
			})
			await onSubmitted()
			onClose()
		} catch (error) {
			setSubmitting(false)
			console.error(error)
			await alert.show("Error", "Failed to edit the channel. Please try again.")
		}
		setSubmitting(false)
	}

	return (
		<DefaultModal onClose={onClose} isOpen={isOpen} width={680} onSubmit={handleSubmit}>
			<ModalTitle>Edit channel</ModalTitle>
			{memberships.error || products.error ? (
				<>
					<ModalText>Failed to load data.</ModalText>
					<ModalButtons>
						<Button variant="secondary" onClick={onClose}>
							Close
						</Button>
					</ModalButtons>
				</>
			) : memberships.data === undefined || products.data === undefined ? (
				<PartialLoadingPage />
			) : (
				<>
					<ModalInputs>
						<InputField label="Channel name" value={name} onChange={setName} />
						<ChannelAccessInputs
							memberships={memberships.data}
							products={products.data}
							defaultAccess={defaultAccess}
							membershipAccess={membershipAccess}
							productAccess={productAccess}
							onDefaultAccessChange={setDefaultAccess}
							onMembershipAccessChange={setMembershipAccess}
							onProductAccessChange={setProductAccess}
						/>
					</ModalInputs>
					<ModalButtons>
						<Button variant="secondary" onClick={onClose} isDisabled={isSubmitting}>
							Cancel
						</Button>
						<SubmitButton variant="primary" isLoading={isSubmitting}>
							Save
						</SubmitButton>
					</ModalButtons>
				</>
			)}
		</DefaultModal>
	)
}

export default EditModal
